<template>
    <v-dialog
        transition="dialog-bottom-transition"
        max-width="900px"
        v-model="dialog"
    >
        <template v-slot:activator="{ props: activatorProps }">
            <v-list-item v-bind="activatorProps">
                <v-list-item-title>
                    <v-icon class="mr-2">mdi-message-text</v-icon>
                    Observaciones
                </v-list-item-title>
            </v-list-item>
        </template>

        <template v-slot:default="{ isActive }">
            <v-card>
                <v-toolbar title="Observaciones de la solicitud" class="modal-header">
                    <template v-slot:append>
                        <v-btn icon @click="isActive.value = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                </v-toolbar>

                <v-card-text class="pa-6">
                    <!-- Sección de observaciones -->
                    <div v-if="observaciones && observaciones.length > 0">
                        <h3 class="my-text-black mb-4">
                            <v-icon color="#254126" class="mr-2">mdi-eye</v-icon>
                            Historial de observaciones
                        </h3>
                        <v-data-table 
                            :headers="headers"
                            :items="observaciones"
                            hide-default-footer
                            class="elevation-1"
                            :items-per-page="-1"
                            disable-sort="true"
                        >
                            <template v-slot:[`item.fecha`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-icon size="small" class="mr-2" color="#254126">mdi-calendar</v-icon>
                                    {{ formatearFecha(item.fecha) }}
                                </div>
                            </template>

                            <template v-slot:[`item.username`]="{ item }">
                                <div class="d-flex align-center">
                                    <v-icon size="small" class="mr-2" color="#254126">mdi-account</v-icon>
                                    {{ item.username }}
                                </div>
                            </template>

                            <template v-slot:[`item.motivo`]="{ item }">
                                <div class="observacion-texto">
                                    {{ item.motivo }}
                                </div>
                            </template>
                            
                            <template v-slot:[`item.id`]="{ item }">
                                <div v-if="esRolAdministrativo()" class="d-flex justify-center">
                                    <v-btn
                                        :icon="item.resuelta ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"
                                        :color="item.resuelta ? 'green' : 'grey'"
                                        variant="text"
                                        @click="cambiarResolucion(item)"
                                        :loading="cargando"
                                        :disabled="cargando"
                                    >
                                    </v-btn>
                                </div>
                                <div v-else class="d-flex justify-center">
                                    <v-chip
                                        :color="item.resuelta ? 'green' : 'orange'"
                                        :prepend-icon="item.resuelta ? 'mdi-check-circle' : 'mdi-alert-circle'"
                                        size="small"
                                    >
                                        {{ item.resuelta ? 'Resuelta' : 'Pendiente' }}
                                    </v-chip>
                                </div>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- Mensaje cuando no hay observaciones -->
                    <div v-else class="recuadro my-4">
                        <v-icon size="64" color="#254126" class="mb-3">mdi-check-circle-outline</v-icon>
                        <p class="my-text-black text-h6">No hay observaciones registradas</p>
                        <p class="text-body-2">Esta solicitud no tiene observaciones pendientes</p>
                    </div>

                    <!-- Sección de motivo de rechazo -->
                    <div v-if="motivoRechazo" class="mt-6">
                        <v-divider class="mb-4"></v-divider>
                        <h3 class="my-text-black mb-3">
                            <v-icon color="red" class="mr-2">mdi-close-circle</v-icon>
                            Motivo de rechazo
                        </h3>
                        <v-alert
                            type="error"
                            variant="tonal"
                            prominent
                            border="start"
                        >
                            <div class="text-body-1">
                                {{ motivoRechazo }}
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        id="boton-verde"
                        @click="isActive.value = false"
                        :disabled="cargando"
                        :loading="cargando"
                    >
                        Cerrar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </template>
    </v-dialog>
</template>

<style scoped>
.observacion-texto {
    max-width: 400px;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.5;
    padding: 8px 0;
}

.v-data-table {
    border-radius: 8px;
}

/* Estilos adicionales para las filas de la tabla */
:deep(.v-data-table__tr:hover) {
    background-color: #f5f5f5 !important;
}

:deep(.v-data-table-header) {
    background-color: #edf4ed;
}

:deep(.v-data-table-header th) {
    color: #254126 !important;
    font-weight: 600 !important;
}
</style>

<style src="../assets/general.css"></style>

<script>
    import PrestacionService from '@/api/PrestacionService';
    import { Role } from '@/_helpers/role';

    const prestacionService = new PrestacionService();

    export default {
        data: () => ({
            dialog: false,
            cargando: false,
            observaciones: [],
            headers: [
                { title: "Fecha", key: "fecha", width: "15%" },
                { title: "Usuario", key: "username", width: "20%" },
                { title: "Observación", key: "motivo", width: "50%" }
            ]
        }),
        props: {
            idSolicitud: {
                type: Number,
                required: true,
            },
            motivoRechazo: {
                type: String,
                default: ''
            }
        },
        async mounted(){
            await this.getObservaciones();
            if(!this.esRolAutorizador()){
                this.headers.push({ title: "Resuelta", key: "id", width: "15%", align: "center" });
            } else {
                this.headers.push({ title: "Estado", key: "id", width: "15%", align: "center" });
            }
        },
        methods: {
            async getObservaciones(){
                this.cargando = true;
                const response = await prestacionService.getObservaciones(this.idSolicitud);
                if(response && response.status == 200){
                    this.observaciones = response.data;
                }
                this.cargando = false;
            },
            esRolAutorizador(){
                return this.$store.getters.getRole === Role.Autorizador;
            },
            esRolAdministrativo(){
                return this.$store.getters.getRole === Role.AdministrativoFacturador;
            },
            async cambiarResolucion(item){
                this.cargando = true;
                const response = await prestacionService.cambiarObservacion(item.id, item.resuelta);
                if(response && response.status == 200){
                    item.resuelta = !item.resuelta;
                    await this.getObservaciones();
                }
                this.cargando = false;
            },
            formatearFecha(fecha) {
                const date = new Date(fecha);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            }
        }
    }
</script>